import { Box, Button, Chip, Snackbar, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useRedirect } from 'react-admin';
import useDebounce from '../../../../hooks/useDebounce';
import { Pagination } from '../../../../interfaces/common';
import { Medication, MedicationStatus } from '../../../../interfaces/health-data';
import healthDataService from '../../../../services/health-data';
import { DEFAULT_PAGE_SIZE, INITIAL_PAGINATION_STATE } from '../../../../util/constants';
import { getCapitalizedWord } from '../../../../util/stringFormat';
import { CustomTable } from '../../../common/CustomTable';
import './css/medication.css';

export const Medications = () => {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const redirect = useRedirect();

  const [currentSearchString, setCurrentSearchString] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION_STATE);

  const fetchMedications = async ({ searchString }: { searchString?: string } = {}) => {
    if (searchString) {
      setCurrentSearchString(searchString);
    }

    const response = await healthDataService.findAllMedicationsByParams({
      ...(searchString ? { searchString } : {}),
      status: MedicationStatus.ACTIVE,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
    });

    if (!response.data) {
      setMessage(response.message || 'There is a problem fetching medications');
      return;
    }
    setPagination(response.data.pagination);
    setMedications(response.data.medications);
  };

  const debouncedFetchMedications = useDebounce(fetchMedications);

  const handleChangePage = (page: number) => setPage(page);

  useEffect(() => {
    (async () => await fetchMedications({ searchString: currentSearchString }))();
  }, [page]);

  return (
    <>
      <Box className='tab-section'>
        <Box className='medications-header'>
          <Typography>Medications</Typography>
          <Box>
            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography
                mb={1}
                sx={{
                  '&.MuiTypography-root': { fontSize: '14px' },
                }}>
                Search by name, NDC code or RxCUI Code
              </Typography>
              <TextField
                key={`autocomplete-medications`}
                size='small'
                placeholder='Type a name, ndc code or rxCui Code'
                sx={{
                  width: '400px',
                  minWidth: '250px',
                }}
                onChange={(e) =>
                  debouncedFetchMedications({
                    searchString: e.target.value,
                  })
                }
              />
            </Box>

            <Button
              variant='contained'
              onClick={() => redirect(`/medical-setup/medications/create`)}>
              Add Medication
            </Button>
          </Box>
        </Box>

        <CustomTable
          columns={[
            { key: 'status', label: 'Status' },
            { key: 'conceptName', label: 'Name' },
            { key: 'ndcCode', label: 'NDC Code' },
            { key: 'rxCuiCode', label: 'RxCUI Code' },
            { key: 'actions', label: 'Actions', width: '150px', minWidth: '150px' },
          ]}
          defaultText='No medications created'
          key={'medications-table'}
          rows={medications.map((data) => {
            const { status, conceptName, rxCuiCode, ndcCode } = data;

            return {
              status: (
                <Chip
                  label={getCapitalizedWord(status)}
                  sx={{
                    color: 'white',
                    backgroundColor: status === MedicationStatus.ACTIVE ? '#1FAF84' : '#FF4A47',
                  }}
                />
              ),
              conceptName,
              ndcCode,
              rxCuiCode,
              actions: [
                <Button
                  variant='text'
                  onClick={() => redirect(`/medical-setup/medications/${data?.id}/show`)}
                  sx={{
                    textTransform: 'capitalize',
                  }}>
                  View
                </Button>,
                <Button
                  variant='text'
                  onClick={() => redirect(`/medical-setup/medications/${data?.id}/edit`)}
                  sx={{
                    textTransform: 'capitalize',
                  }}>
                  Edit
                </Button>,
              ],
            };
          })}
          pagination={{
            rowsPerPageOptions: [DEFAULT_PAGE_SIZE],
            totalCount: pagination.totalCount,
            rowPerPage: pagination.perPage,
            page: pagination.currentPage,
            onPageChange: handleChangePage,
          }}
        />
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!message}
        autoHideDuration={10000}
        onClose={() => setMessage('')}
        message={message}
        ContentProps={{
          style: { marginTop: '50px' },
        }}
      />
    </>
  );
};
